import React, { SyntheticEvent } from "react";
import { Checkbox, Form, FormField, FormGroup, Input, Radio } from "semantic-ui-react";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import TimeComboBox from "react-lib/apps/common/TimeComboBox";
import ColorPicker from "./ColorPicker";

// Interface
import { SetProp, PickedProps } from "./sequence/WorkSchedule";

// Serializer
import WorkShiftTemplateSerializer from "issara-sdk/types/WorkShiftTemplateSerializer_apps_HRM";

type SetupWorkShiftsTemplatePanelProps = {
  setProp: SetProp;
  // func
  onSave: (e: any) => void;
  onEdit: (e: any) => void;
  onDelete: (e: any) => void;
  onClear: (e: any) => void;
  onChange: (e: SyntheticEvent | null, data: Record<string, any>) => void;
  // data
  data: Partial<WorkShiftTemplateSerializer>;
  totalWorking: { hours: number; minutes: number };
  // config
  paramKey: string;
} & PickedProps;

const SetupWorkShiftsTemplatePanel = (props: SetupWorkShiftsTemplatePanelProps) => {
  const { data, totalWorking } = props;
  const isNew = !data.id;
  const isUsed = !!data.is_in_use;
  const isFullEditable = isNew || !isUsed;

  return (
    <div style={{ backgroundColor: "#bfeafe", height: "100vh", overflow: "auto" }}>
      <Form style={{ paddingLeft: "1rem", paddingTop: "1rem", paddingRight: "1rem" }}>
        <FormGroup>
          <FormField>
            <label id="SetupWorkShiftsTemplatePanel-Label-Title" style={{ fontSize: "18px", fontWeight: "bold" }}>Setup Work Shifts</label>
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField width={16}>
            <label>Shift Code</label>
            <Input
              id="SetupWorkShiftsTemplatePanel-Input-ShiftCode"
              disabled={!isFullEditable}
              style={{ width: "100%" }}
              name="code"
              onChange={props.onChange}
              value={data?.code || ""}
            />
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField width={16}>
            <label>Shift Name</label>
            <Input
              id="SetupWorkShiftsTemplatePanel-Input-ShiftName"
              disabled={!isFullEditable}
              style={{ width: "100%" }}
              name="name"
              onChange={props.onChange}
              value={data?.name || ""}
            />
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField width={16}>
            <label>Work Time</label>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
              <TimeComboBox
                id="SetupWorkShiftsTemplatePanel-TimeComboBox-StartTime"
                disabled={!isFullEditable}
                style={{ width: "30%" }}
                onTextChange={(value: any) => props.onChange?.(null, { name: "start_time", value })}
                defaultValue={data?.start_time}
              />
              <label>to</label>
              <TimeComboBox
                id="SetupWorkShiftsTemplatePanel-TimeComboBox-EndTime"
                disabled={!isFullEditable}
                style={{ width: "30%" }}
                onTextChange={(value: any) => props.onChange?.(null, { name: "end_time", value })}
                defaultValue={data?.end_time}
              />
            </div>
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField width={16}>
            <Checkbox
              id="SetupWorkShiftsTemplatePanel-Checkbox-IsBreakTime"
              disabled={!isFullEditable}
              label="Set break time (Deduct working hours from work time)"
              style={{ fontWeight: "bold" }}
              name="is_break_time"
              onChange={props.onChange}
              checked={data?.is_break_time || false}
            />
            {!!data?.is_break_time && (
              <div
                style={{ display: "flex", gap: "1rem", alignItems: "center", marginTop: "0.5rem" }}
              >
                <TimeComboBox
                  id="SetupWorkShiftsTemplatePanel-TimeComboBox-StartBreakTime"
                  disabled={!isFullEditable}
                  style={{ width: "30%" }}
                  onTextChange={(value: any) =>
                    props.onChange?.(null, { name: "start_break_time", value })
                  }
                  defaultValue={data?.start_break_time || undefined}
                />
                <label>to</label>
                <TimeComboBox
                  id="SetupWorkShiftsTemplatePanel-TimeComboBox-EndBreakTime"
                  disabled={!isFullEditable}
                  style={{ width: "30%" }}
                  onTextChange={(value: any) =>
                    props.onChange?.(null, { name: "end_break_time", value })
                  }
                  defaultValue={data?.end_break_time || undefined}
                />
              </div>
            )}
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField width={16}>
            <div style={{ display: "flex", gap: "1rem", alignItems: "center", fontWeight: "bold" }}>
              <label>Total working hours</label>
              <Input id="SetupWorkShiftsTemplatePanel-Input-TotalHour" style={{ width: "12%" }} readOnly value={totalWorking?.hours ?? 0} />
              <label>hr(s)</label>
              <Input id="SetupWorkShiftsTemplatePanel-Input-TotalMinute" style={{ width: "12%" }} readOnly value={totalWorking?.minutes ?? 0} />
              <label>min(s)</label>
            </div>
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField inline>
            <label>Display Color:</label>
          </FormField>
          <FormField inline width={6}>
            <ColorPicker
              id="SetupWorkShiftsTemplatePanel-ColorPicker"
              style={{ width: "100%" }}
              value={data?.color_code}
              onChange={(value: any) => props.onChange?.(null, { name: "color_code", value })}
            />
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField inline>
            <label>Shift Type:</label>
          </FormField>
          <FormField inline>
            <Radio
              id="SetupWorkShiftsTemplatePanel-Radio-Day"
              disabled={!isFullEditable}
              label="Day"
              style={{ fontWeight: "bold" }}
              name="shift_type"
              value="D"
              checked={data?.shift_type === "D"}
              onChange={props.onChange}
            />
          </FormField>
          <FormField inline>
            <Radio
              id="SetupWorkShiftsTemplatePanel-Radio-Night"
              disabled={!isFullEditable}
              label="Night"
              style={{ fontWeight: "bold" }}
              name="shift_type"
              value="N"
              checked={data?.shift_type === "N"}
              onChange={props.onChange}
            />
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField>
            <Checkbox
              id="SetupWorkShiftsTemplatePanel-Checkbox-Active"
              label="Active"
              style={{ fontWeight: "bold" }}
              name="active"
              checked={data?.active || false}
              onChange={props.onChange}
            />
          </FormField>
        </FormGroup>
        <FormGroup>
          <FormField width={16}>
            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: "10px", width: "100%" }}
            >
              <ButtonLoadCheck
                id="SetupWorkShiftsTemplatePanel-ButtonLoadCheck-Clear"
                // func
                setProp={props.setProp}
                onClick={props.onClear}
                // data
                paramKey={`${props.paramKey}_CLEAR`}
                buttonLoadCheck={props.buttonLoadCheck?.[`${props.paramKey}_CLEAR`]}
                // config
                color="orange"
                title="CLEAR"
                name="CLEAR"
                size="medium"
              />
              {data.id && !data.is_in_use && (
                <ButtonLoadCheck
                  id="SetupWorkShiftsTemplatePanel-ButtonLoadCheck-Delete"
                  // func
                  setProp={props.setProp}
                  onClick={props.onDelete}
                  // data
                  paramKey={`${props.paramKey}_DELETE`}
                  buttonLoadCheck={props.buttonLoadCheck?.[`${props.paramKey}_DELETE`]}
                  // config
                  color="red"
                  title="DELETE"
                  name="DELETE"
                  size="medium"
                />
              )}
              <ButtonLoadCheck
                id={`SetupWorkShiftsTemplatePanel-ButtonLoadCheck-${data.id ? "Edit" : "Save"}`}
                // func
                setProp={props.setProp}
                onClick={data.id ? props.onEdit : props.onSave}
                // data
                paramKey={`${props.paramKey}_${data.id ? "EDIT" : "SAVE"}`}
                buttonLoadCheck={
                  props.buttonLoadCheck?.[`${props.paramKey}_${data.id ? "EDIT" : "SAVE"}`]
                }
                // config
                color={data.id ? "yellow" : "green"}
                title={data.id ? "EDIT" : "SAVE"}
                name={data.id ? "EDIT" : "SAVE"}
                size="medium"
              />
            </div>
          </FormField>
        </FormGroup>
      </Form>
    </div>
  );
};

export default React.memo(SetupWorkShiftsTemplatePanel);
